import {Injectable} from "@angular/core";
import {lastValueFrom} from "rxjs";
import {HttpClient} from "@angular/common/http";
import { Logger, Platform, ToastService } from "shared";
import { File } from "@ionic-native/file/ngx";
import { FileOpener } from "@ionic-native/file-opener/ngx";

@Injectable({
  providedIn: 'root'
})
export class DownloadService {

  protected logger = new Logger('DownloadService');

  constructor(private httpClient: HttpClient, 
              private platform: Platform, 
              private file: File,
              private fileOpener: FileOpener,
              private toastService: ToastService) {
  }

  download(url: string, 
          host: Element, 
          options?: Partial<{ 
            filename: string, 
            unblockDelay:number, 
            successMessage: string, 
            errorMessage: string
            open: boolean
          }>
  ): Promise<Blob> {
    const DOWNLOADING = "downloading";
    if (!host.classList.contains(DOWNLOADING)) {
        host.classList.add(DOWNLOADING);
        return new Promise<Blob>((resolve, reject) => {
            this.downloadBlob(url)
              .then(async([blob, filename]) => {
                filename = filename || options?.filename || url.substring(url.lastIndexOf('/')+1);
                if (this.platform.is('hybrid') && this.platform.is('ios')) {
                  const fileDir = this.file.documentsDirectory; // or this.file.dataDirectory
                  this.logger.debug('Download completed. Saving document to filesystem... ', { fileDir, filename });
                  await this.file.writeFile(fileDir, filename, blob, { replace: true });
                  if (options?.open || options?.successMessage) {
                    const actions = [];
                    if (options?.open) {
                      actions.push({
                        label: 'actions.open',
                        callback: async() => {
                          const filePath = fileDir + filename;
                          const  mimeType = this.getMimeType(filename) || blob.type || 'application/octet-stream';
                          await this.fileOpener.open(filePath, mimeType);
                        },
                        class: 'btn-outline-success'
                      })
                    }
                    this.toastService.info(options?.successMessage || 'download.completed', '', actions );
                  }
                } else {
                  let anchor = document.createElement("a");
                  host.appendChild(anchor);
                  anchor.style.display = "none";
                  const blobUrl = window.URL.createObjectURL(blob);
                  anchor.href = blobUrl;
                  anchor.download = filename;
                  anchor.click();
                  window.URL.revokeObjectURL(blobUrl);
                  anchor.remove();
                //   this.toastService.info(options?.successMessage || 'download.completed', '', [] );
                }
                resolve(blob);
              })
              .catch((error) => {
                if (options?.errorMessage) {
                  this.toastService.error(options.errorMessage);
                }
                reject(error);
              })
              .finally(()=>{
                window.setTimeout(
                    () => host.classList.remove(DOWNLOADING),
                    options?.unblockDelay && typeof options.unblockDelay === 'number'
                        ? Math.min(10_000,Math.max(100, options.unblockDelay))
                        : 2000   // 2 seconds delay by default
                );
              });
        });
    }
    return Promise.reject("download in progress");
  }

  downloadBlob(url: string): Promise<[Blob, string | undefined]> {
    if (url.length > 0) {
      return lastValueFrom(this.httpClient.get(url,{ responseType: 'blob', observe: 'response' }))
          .then((response) => {
              const result: [Blob, string | undefined] = [response.body!, undefined];
              const contentDisposition = response.headers.get('"Content-Disposition"');
              if (contentDisposition &&
                 (contentDisposition.indexOf('attachment') !== -1 ||
                  contentDisposition.indexOf('inline') !== -1)) {
                  const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                  const matches = filenameRegex.exec(contentDisposition);
                  if (matches != null && matches[1]) {
                      result[1] = matches[1].replace(/['"]/g, '');
                  }
              }
              return result;
            })
    } else {
      return Promise.reject("failed to download blob");
    }
  }

  private getMimeType(filePath: string): string {
    const extension = filePath.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf': return 'application/pdf';
      case 'jpg':
      case 'jpeg':
      case 'png': return 'image/jpeg';
      case 'doc':
      case 'docx': return 'application/msword';
      case 'txt': return 'text/plain';
      default: return 'application/octet-stream';
    }
  }
}
